import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuthentication } from '@/features/auth/AuthenticationContext'
import { useToast } from '@/hooks/use-toast'

import { getAppConfig } from '@/lib/app-config'

export function LoginPage() {
  const isMockAuth = getAppConfig().mockAuth
  const [isSigningIn, setIsSigningIn] = useState(false)
  const navigate = useNavigate()
  const { toast } = useToast()
  const { login, mockLogin, isLoading } = useAuthentication()

  const handleMockLogin = async (token: string) => {
    setIsSigningIn(true)
    try {
      await mockLogin(token)
      toast({
        title: 'Welcome!',
        description: `Signed in as ${token === 'admin@dev' ? 'Dev Admin' : 'Dev User'}.`,
      })
      navigate('/')
    } catch (error: any) {
      toast({
        title: 'Sign-in failed',
        description:
          error.response?.data?.detail ||
          error.response?.data?.error ||
          error.message ||
          'An error occurred during sign-in',
        variant: 'destructive',
      })
    } finally {
      setIsSigningIn(false)
    }
  }

  const loading = isLoading || isSigningIn

  return (
    <div className="bg-white p-8 rounded-lg shadow-md">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900">
          Sign in to your account
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          {isMockAuth
            ? 'Development mode — Azure AD is bypassed'
            : 'Use your organization\'s Microsoft account to sign in'}
        </p>
      </div>

      {isMockAuth ? (
        <div className="space-y-3">
          <Button
            onClick={() => handleMockLogin('admin@dev')}
            className="w-full flex items-center justify-center gap-3 py-6 bg-amber-600 hover:bg-amber-700"
            disabled={loading}
            size="lg"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Signing in…
              </>
            ) : (
              <>🛡️ Dev Admin (Full Access)</>
            )}
          </Button>
          <Button
            onClick={() => handleMockLogin('user@dev')}
            className="w-full flex items-center justify-center gap-3 py-6 bg-slate-600 hover:bg-slate-700"
            disabled={loading}
            size="lg"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Signing in…
              </>
            ) : (
              <>👤 Dev User (Limited Access)</>
            )}
          </Button>
        </div>
      ) : (
        <Button
          onClick={() => login()}
          className="w-full flex items-center justify-center gap-3 py-6"
          disabled={loading}
          size="lg"
        >
          {loading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Redirecting to Microsoft…
            </>
          ) : (
            <>
              {/* Microsoft logo */}
              <svg className="h-5 w-5" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
              </svg>
              Sign in with Microsoft
            </>
          )}
        </Button>
      )}

      <p className="mt-6 text-xs text-center text-gray-400">
        {isMockAuth
          ? '⚠️ Mock authentication — do not use in production'
          : 'Protected by Azure Active Directory'}
      </p>
    </div>
  )
}
